import React, { useMemo, useState } from "react";
import {
  Stack,
  Text,
  Group,
  ActionIcon,
  TextInput,
  Badge,
  Tooltip,
  Box,
} from "@mantine/core";
import {
  IconGripVertical,
  IconPencil,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import type { Menu, MenuItem } from "./types";
import {
  getItemCategories,
  getItemEnglishCategories,
  readCategoryOrder,
} from "./hooks/useMenuV2";

interface Props {
  menu: Menu;
  onRename: (oldName: string, newName: string) => Promise<void>;
  onReorder: (next: string[]) => Promise<void>;
}

type Scope = "source" | "english";

const collectCategories = (
  menu: Menu,
  scope: Scope
): { ordered: string[]; counts: Record<string, number> } => {
  const present = new Set<string>();
  const counts: Record<string, number> = {};
  (menu.menuPages || []).forEach((p) =>
    (p.menuItems || []).forEach((it: MenuItem) => {
      const cats =
        scope === "source"
          ? getItemCategories(it)
          : getItemEnglishCategories(it);
      cats.forEach((c) => {
        present.add(c);
        counts[c] = (counts[c] || 0) + 1;
      });
    })
  );

  // Apply saved order; categories present but not in saved order get appended alphabetically
  const savedOrder = readCategoryOrder(menu.categoryOrder);
  const savedFiltered = savedOrder.filter((c) => present.has(c));
  const seen = new Set(savedFiltered);
  const extras = Array.from(present)
    .filter((c) => !seen.has(c))
    .sort((a, b) => a.localeCompare(b));
  return { ordered: [...savedFiltered, ...extras], counts };
};

const CategoryList: React.FC<{
  scope: Scope;
  title: string;
  hint?: string;
  menu: Menu;
  onRename: (oldName: string, newName: string) => Promise<void>;
  onReorder: (next: string[]) => Promise<void>;
}> = ({ scope, title, hint, menu, onRename, onReorder }) => {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const { ordered, counts } = useMemo(
    () => collectCategories(menu, scope),
    [menu, scope]
  );

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    // Reorder within this scope, then preserve the order of the OTHER scope.
    const reorderedScope = Array.from(ordered);
    const [moved] = reorderedScope.splice(result.source.index, 1);
    reorderedScope.splice(result.destination.index, 0, moved);

    // Build the combined categoryOrder: keep current saved order, but replace
    // this scope's positions with the new ordering. Categories outside this
    // scope stay where they were relative to each other.
    const savedOrder = readCategoryOrder(menu.categoryOrder);
    const scopeSet = new Set(ordered);
    const others = savedOrder.filter((c) => !scopeSet.has(c));
    await onReorder([...others, ...reorderedScope]);
  };

  const startEdit = (name: string) => {
    setEditing(name);
    setDraft(name);
  };
  const cancelEdit = () => {
    setEditing(null);
    setDraft("");
  };
  const commitEdit = async (oldName: string) => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === oldName) {
      cancelEdit();
      return;
    }
    await onRename(oldName, trimmed);
    cancelEdit();
  };

  return (
    <div>
      <Group justify="space-between" mb={4} align="center">
        <Group gap={6}>
          <Text size="xs" fw={600} tt="uppercase" c="dimmed">
            {title}
          </Text>
          <Badge size="xs" variant="outline" color="gray">
            {ordered.length}
          </Badge>
        </Group>
      </Group>
      {hint && (
        <Text size="xs" c="dimmed" mb="xs">
          {hint}
        </Text>
      )}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={`cat-list-${scope}`}>
          {(provided) => (
            <Stack
              gap={4}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {ordered.map((name, idx) => (
                <Draggable
                  key={`${scope}-${name}`}
                  draggableId={`${scope}-${name}`}
                  index={idx}
                >
                  {(p, snapshot) => (
                    <Box
                      ref={p.innerRef}
                      {...p.draggableProps}
                      style={{
                        ...p.draggableProps.style,
                        padding: "6px 8px",
                        borderRadius: 6,
                        background: snapshot.isDragging
                          ? "var(--mantine-color-orange-1)"
                          : "white",
                        border: "1px solid var(--mantine-color-gray-3)",
                      }}
                    >
                      <Group gap={6} wrap="nowrap">
                        <Box
                          {...p.dragHandleProps}
                          style={{ cursor: "grab", display: "flex" }}
                        >
                          <IconGripVertical
                            size={14}
                            color="var(--mantine-color-gray-5)"
                          />
                        </Box>
                        {editing === name ? (
                          <>
                            <TextInput
                              size="xs"
                              value={draft}
                              onChange={(e) =>
                                setDraft(e.currentTarget.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") commitEdit(name);
                                if (e.key === "Escape") cancelEdit();
                              }}
                              autoFocus
                              style={{ flex: 1 }}
                            />
                            <Tooltip label="Save">
                              <ActionIcon
                                size="sm"
                                variant="subtle"
                                color="green"
                                onClick={() => commitEdit(name)}
                              >
                                <IconCheck size={14} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Cancel">
                              <ActionIcon
                                size="sm"
                                variant="subtle"
                                color="gray"
                                onClick={cancelEdit}
                              >
                                <IconX size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Text size="sm" style={{ flex: 1 }} lineClamp={1}>
                              {name}
                            </Text>
                            <Badge size="xs" variant="light" color="gray">
                              {counts[name] || 0}
                            </Badge>
                            <Tooltip label="Rename">
                              <ActionIcon
                                size="sm"
                                variant="subtle"
                                color="gray"
                                onClick={() => startEdit(name)}
                              >
                                <IconPencil size={12} />
                              </ActionIcon>
                            </Tooltip>
                          </>
                        )}
                      </Group>
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              {ordered.length === 0 && (
                <Text size="xs" c="dimmed" ta="center" py="sm">
                  None
                </Text>
              )}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export const CategoryManager: React.FC<Props> = ({
  menu,
  onRename,
  onReorder,
}) => {
  return (
    <Stack gap="md">
      <CategoryList
        scope="source"
        title="Source / JP"
        hint="Original-language categories from item.categories"
        menu={menu}
        onRename={onRename}
        onReorder={onReorder}
      />
      <CategoryList
        scope="english"
        title="English"
        hint="Translated categories from props.translations.en"
        menu={menu}
        onRename={onRename}
        onReorder={onReorder}
      />
    </Stack>
  );
};
